// Ядро расчёта по пресету комиссии — портировано дословно из прототипа
// (правила расчёта), без парсинга выражений/eval.
//
// Вся арифметика — через decimal.js (точная десятичная арифметика), а не
// через нативные JS-числа с плавающей запятой. Обычный "0.1 + 0.2" даёт
// 0.30000000000000004 — на цепочке из курса, процента и нескольких
// умножений/делений такой мусор в младших разрядах гарантирован и хорошо
// заметен операторам (например "0.010950000000000001" вместо "0.01095").
// Decimal считает точно и такого не производит. Публичный контракт модуля
// не меняется — на входе/выходе как были, так и остаются обычные JS Number
// (компоненты и шаблоны их не трогают иначе), Decimal — только внутри.
import Decimal from "decimal.js";

// Первый диапазон, куда попадает sourceVal. to === null — верхняя граница
// открыта (до бесконечности). sourceVal может быть числом или Decimal —
// приводим один раз на входе.
export function selectRange(ranges, sourceVal) {
  if (!Array.isArray(ranges)) return null;
  const val = sourceVal instanceof Decimal ? sourceVal : new Decimal(sourceVal);
  return (
    ranges.find(r => {
      const to = r.to === null || r.to === undefined ? null : new Decimal(r.to);
      return val.gte(r.from) && (to === null || val.lte(to));
    }) || null
  );
}

// percent — напрямую; fixed — конвертируется в эквивалентный процент от
// sourceVal, чтобы дальше вся арифметика шла единообразно в процентах.
// Возвращает обычное число (percent показывается в UI как есть).
export function effectivePercent(range, sourceVal) {
  if (!range) return null;
  if (range.type === "percent") return range.value;
  if (range.type === "fixed") {
    const val = sourceVal instanceof Decimal ? sourceVal : new Decimal(sourceVal);
    if (val.lte(0)) return 0;
    return new Decimal(range.value).div(val).times(100).toNumber();
  }
  return null;
}

// giveRateToRub/getRateToRub — Valutes.course обеих валют пары (курс к
// рублю у каждой по отдельности). marketRate (getKey за 1 giveKey) —
// giveRateToRub/getRateToRub.
//
// editedField — какое поле сейчас редактирует пользователь: 'give' или
// 'get'. Определяет, какое из двух полей — неизвестное, которое нужно
// вычислить. НО диапазон/комиссия при этом ВСЕГДА считаются по сумме
// "даёт клиент" (giveKey) — осознанное бизнес-решение: объём сделки для
// тарифа это то, что клиент нам передаёт, а не то, что мы ему отдаём, и не
// то, какое поле оператор физически печатает прямо сейчас (раньше было
// иначе — диапазон "уезжал" в зависимости от того, из какого поля считали).
//
// Границы диапазонов в пресете ВСЕГДА в рублях — независимо от валют самой
// пары (осознанное решение: тарифные пороги бизнес мыслит в рублях, а не в
// валюте конкретной сделки).
//
// extraGiveFee — "безусловный тариф" из прототипа (там — extra_btc,
// хардкожен под BTC, т.к. единственная пара, где он реально использовался в
// демо-данных, была BTC-BTC). Здесь — generic-поле диапазона, в валюте
// giveKey, применяется поверх percent/fixed независимо от типа диапазона; у
// подавляющего большинства пресетов 0 (см. в прототипе: везде extra_btc:"0",
// кроме BTC-BTC).
//
// Порядок операций над суммой "даёт клиент" (giveKey) — по явному запросу
// пользователя, взамен более ранней версии:
//   1. Считаем денежную величину комиссии (percent — % от ИСХОДНОЙ суммы,
//      fixed — configured-значение в рублях, переведённое в giveKey).
//   2. Вычитаем эту комиссию.
//   3. Вычитаем безусловный тариф (extraGiveFee, уже в giveKey).
//   4. Остаток конвертируем по курсу в getKey.
// Раньше сначала вычитался extraGiveFee, а % считался уже от уменьшенной
// суммы — из-за этого "5.5% от 0.009" не совпадало с тем, что реально
// показывалось (комиссия считалась от 0.009-extraGiveFee, а не от 0.009).
function commissionAmountFor(range, giveAmountD, giveRate) {
  if (range.type === "percent") {
    return giveAmountD.times(range.value).div(100);
  }
  // fixed — range.value всегда в рублях (см. схему CommissionPresets),
  // переводим в giveKey по курсу giveRateToRub.
  return new Decimal(range.value).div(giveRate);
}

export function calculatePresetConversion({
  preset,
  giveRateToRub,
  getRateToRub,
  amount,
  editedField
}) {
  if (!preset || !giveRateToRub || !getRateToRub || !amount || amount <= 0) return null;

  const giveRate = new Decimal(giveRateToRub);
  const getRate = new Decimal(getRateToRub);
  const marketRate = giveRate.div(getRate);
  const amountD = new Decimal(amount);

  if (editedField === "give") {
    // Прямая ветка: amount — это и есть искомая база, диапазон ищем сразу.
    const sourceVal = amountD.times(giveRate);
    const range = selectRange(preset.ranges, sourceVal);
    if (!range) {
      return { range: null, percent: null, result: null, extraGiveFee: null, commissionAmount: null };
    }
    const percent = effectivePercent(range, sourceVal);
    const extraGiveFeeD = new Decimal(range.extraGiveFee || 0);
    const commissionAmount = commissionAmountFor(range, amountD, giveRate);
    const afterCommission = amountD.minus(commissionAmount);
    // Отдаваемая сумма (уже за вычетом комиссии) не покрывает даже
    // безусловный тариф — результат 0, а не отрицательное число. Флажок
    // нужен для UI, иначе "0" выглядит как обычный расчёт, а не как "сумма
    // ниже минимального порога обмена".
    const belowMinimum = afterCommission.lte(extraGiveFeeD);
    const effectiveGive = Decimal.max(afterCommission.minus(extraGiveFeeD), 0);
    const result = effectiveGive.times(marketRate);
    return {
      range,
      percent,
      result: result.toNumber(),
      extraGiveFee: extraGiveFeeD.toNumber(),
      belowMinimum,
      commissionAmount: commissionAmount.toNumber()
    };
  }

  // Обратная ветка (editedField === "get"): amount — это то, что клиент
  // должен ПОЛУЧИТЬ, а нужная для диапазона база (сколько он ДАСТ) сама
  // является неизвестной. Решаем итеративно: берём приближение giveAmount
  // по чистому курсу (без комиссии), ищем по нему диапазон, пересчитываем
  // giveAmount уже с этой комиссией по формуле, обратной прямой ветке, ищем
  // диапазон заново — и так, пока диапазон не стабилизируется. Потолок
  // итераций — на случай осцилляции у самой границы диапазона у нетипичных
  // данных; для обычных пресетов сходится за 1-2 шага.
  //
  // Обращение прямой формулы (get = (give - commission - fee) * marketRate):
  //   percent:  give = (get/marketRate + fee) / (1 - p/100)
  //   fixed:    give = get/marketRate + fixedCommission + fee
  // (fixed-комиссия не зависит от giveAmount, поэтому обращается напрямую).
  let giveAmount = amountD.div(marketRate);
  let range = null;
  let percent = null;
  let extraGiveFeeD = new Decimal(0);
  let commissionAmount = new Decimal(0);
  for (let i = 0; i < 20; i++) {
    const sourceVal = giveAmount.times(giveRate);
    const nextRange = selectRange(preset.ranges, sourceVal);
    if (nextRange === range) break;
    range = nextRange;
    if (!range) break;
    percent = effectivePercent(range, sourceVal);
    extraGiveFeeD = new Decimal(range.extraGiveFee || 0);
    const theoreticalGive = amountD.div(marketRate);
    if (range.type === "percent") {
      const p = new Decimal(range.value).div(100);
      if (p.gte(1)) {
        // 100%+ комиссия — получить что-либо в этом диапазоне
        // математически невозможно (сколько ни отдай, комиссия съест всё).
        range = null;
        break;
      }
      giveAmount = theoreticalGive.plus(extraGiveFeeD).div(new Decimal(1).minus(p));
      commissionAmount = giveAmount.times(p);
    } else {
      commissionAmount = new Decimal(range.value).div(giveRate);
      giveAmount = theoreticalGive.plus(commissionAmount).plus(extraGiveFeeD);
    }
  }
  if (!range) {
    return { range: null, percent: null, result: null, extraGiveFee: null, commissionAmount: null };
  }
  return {
    range,
    percent,
    result: giveAmount.toNumber(),
    extraGiveFee: extraGiveFeeD.toNumber(),
    belowMinimum: false,
    commissionAmount: commissionAmount.toNumber()
  };
}

// Тот же перевод, но без пресета — просто по рынку, для базовой
// двусторонней конвертации в калькуляторе, когда подкатегория не выбрана.
export function calculateMarketConversion({ marketRate, amount, editedField }) {
  if (!marketRate || !amount || amount <= 0) return null;
  const rate = new Decimal(marketRate);
  const amountD = new Decimal(amount);
  return (editedField === "give" ? amountD.times(rate) : amountD.div(rate)).toNumber();
}



// WEBPACK FOOTER //
// ./src/utils/commissionCalculator.js